import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useAbpPermissions } from '../contexts/AbpPermissionsContext';
import { AbpPermissions } from '../constants/permissions';

interface AdminMenuItem {
  labelKey: string;
  path: string;
  /** ABP permission(s) required to see this menu item */
  permission?: string | string[];
  /** If true, requires ALL permissions (default: ANY permission) */
  requireAll?: boolean;
}

const adminMenuItems: AdminMenuItem[] = [
  {
    labelKey: 'App.AdminMenu.UploadBook',
    path: '/upload',
    permission: AbpPermissions.UploadBook,
  },
  {
    labelKey: 'App.AdminMenu.BulkLicenseAssignment',
    path: '/licenses/bulk-assign',
    permission: AbpPermissions.BulkAssignLicenses,
  },
  {
    labelKey: 'App.AdminMenu.ManageBooks',
    path: '/admin/books',
    permission: AbpPermissions.ManageBooks,
  },
  {
    labelKey: 'App.AdminMenu.ManagePublishers',
    path: '/admin/publishers',
    permission: AbpPermissions.ManagePublishers,
  },
  {
    labelKey: 'App.AdminMenu.ManageBooksellers',
    path: '/admin/booksellers',
    permission: AbpPermissions.ManageBooksellers,
  },
  {
    labelKey: 'App.AdminMenu.MissingBooksReport',
    path: '/admin/missing-books',
    permission: AbpPermissions.ViewMissingBooks,
  },
  {
    labelKey: 'App.AdminMenu.ManageLicenses',
    path: '/admin/licenses',
    permission: AbpPermissions.ManageLicenses,
  },
  {
    labelKey: 'App.AdminMenu.TestBooksellerAuth',
    path: '/admin/test-bookseller-auth',
    permission: AbpPermissions.ManageBooksellers,
  },
  {
    labelKey: 'App.AdminMenu.TestEntitlementApi',
    path: '/admin/test-entitlement-api',
    permission: AbpPermissions.ManageBooksellers,
  },
  {
    labelKey: 'App.AdminMenu.TtsVoiceConfiguration',
    path: '/admin/tts-config',
    permission: AbpPermissions.ManageBooksellerTts,
  },
  {
    labelKey: 'App.AdminMenu.ViewSessions',
    path: '/admin/sessions',
    permission: AbpPermissions.ViewSessions,
  },
  {
    labelKey: 'App.AdminMenu.HangfireDashboard',
    path: '/hangfire',
    permission: AbpPermissions.ViewHangfireDashboard,
  },
  {
    labelKey: 'App.AdminMenu.AuditLogs',
    path: '/admin/audit-logs',
    permission: AbpPermissions.ViewAuditLogs,
  },
  {
    labelKey: 'App.AdminMenu.SystemSettings',
    path: '/admin/settings',
    permission: AbpPermissions.ManageSystemSettings,
  },
  {
    labelKey: 'App.AdminMenu.Translations',
    path: '/admin/translations',
    permission: AbpPermissions.ManageLanguageTexts,
  },
];

export default function AdminMenu() {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const { hasPermission, hasAnyPermission, hasAllPermissions, isLoading } = useAbpPermissions();

  // Filter menu items based on user's ABP permissions
  const visibleMenuItems = adminMenuItems.filter((item) => {
    if (!item.permission) {
      return true; // No permission required
    }

    const permissions = Array.isArray(item.permission) ? item.permission : [item.permission];

    return item.requireAll
      ? hasAllPermissions(permissions)
      : hasAnyPermission(permissions);
  });

  // Close menu when clicking outside
  // IMPORTANT: This must be called BEFORE any early returns to follow Rules of Hooks
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isOpen]);

  // Don't render menu while loading or if user has no admin access
  if (isLoading || visibleMenuItems.length === 0) {
    return null;
  }

  return (
    <div className="relative" ref={menuRef}>
      {/* Admin Menu Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="text-neutral-700 hover:text-primary-600 px-3 py-2 rounded-md text-sm font-medium transition-colors flex items-center min-h-[44px]"
        aria-haspopup="menu"
        aria-expanded={isOpen}
      >
        {t('App.AdminMenu.Button')}
        <svg
          className={`ml-1 h-4 w-4 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div
          className="absolute right-0 mt-2 w-64 bg-white rounded-md shadow-lg py-1 z-50 border border-neutral-200"
          role="menu"
          aria-label="Admin menu"
        >
          {visibleMenuItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              onClick={() => setIsOpen(false)}
              className="block px-4 py-2 text-sm text-neutral-700 hover:bg-neutral-100 hover:text-primary-600 transition-colors"
              role="menuitem"
            >
              {t(item.labelKey)}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
